import {
   ChangeDetectionStrategy,
   ChangeDetectorRef,
   Component,
   EventEmitter,
   Input,
   Output,
   TemplateRef,
   ViewChild,
   ViewEncapsulation,
} from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';
import { Router } from '@angular/router';
import { ChartDataPoint, ChartSeriesDataPoint } from '@ndt/reports/shared/charts/chart-data-point-interface';
import { ReportParameterFormFactoryService } from '@ndt/reports/shared/report-parameter-list/services/report-parameter-form-factory.service';
import { ReportParameterFormService } from '@ndt/reports/shared/report-parameter-list/services/report-parameter-form.service';
import { Store } from '@ngrx/store';
import { LegendPosition } from '@swimlane/ngx-charts';
import { TileViewModes } from 'generated/graphql-types';
import { takeUntil } from 'rxjs/operators';
import { TileDataUnion } from 'src/app/pages/tiles/models/tile-data.model';
import { TileBase } from 'src/app/pages/tiles/views/shared/tile-base';
import { defaultColorScheme } from '../chart-base';
import { ChartWrapperComponent } from '../chart-wrapper.component';
import { ChartDisplayType } from './chart-tile-map.model';
import { GraphTileService } from './graph-tile.service';
import { PieGraphTileDefaultColors } from './pie-graph-tile-default-colors';

@Component({
   encapsulation: ViewEncapsulation.None,
   changeDetection: ChangeDetectionStrategy.OnPush,
   selector: 'qmg-graph-tile',
   styleUrls: ['./graph-tile.component.scss'],
   templateUrl: './graph-tile.component.html',
   host: {
      'class': 'graph-tile',
      '[class.hasBelowLegendPosition]': 'hasBelowLegendPosition && graphTileService.legend && !fallbackTemplateRef',
   },
   providers: [GraphTileService],
   standalone: false,
})
export class GraphTileComponent<
   P,
   T extends TileDataUnion,
   U extends ChartDataPoint | ChartSeriesDataPoint
> extends TileBase<P, T> {
   @ViewChild('chartWrapper')
   get chartWrapper() {
      return this._chartWrapper;
   }
   set chartWrapper(value: ChartWrapperComponent<U>) {
      this._chartWrapper = value;
      this._chartWrapper?.triggerUpdate;
   }
   private _chartWrapper: ChartWrapperComponent<U>;

   hasBelowLegendPosition: boolean = false;

   @Input('fallbackTemplateRef') fallbackTemplateRef: TemplateRef<any> | null = null;

   @Input('name') name: string = '';

   @Input('formService') formService: ReportParameterFormService<P>;

   @Input() chartDisplayType: ChartDisplayType;

   @Input() displayPieGridIndividualPies: boolean;

   @Input() customLabel: string = 'Total';

   @Input() canClickPieGrid: boolean = false;

   @Input() canClickPieInGridCb: (data: any) => boolean = () => false;

   @Output() chartClick = new EventEmitter<ChartSeriesDataPoint>();

   @Output() select = new EventEmitter<any>();

   ChartDisplayType = ChartDisplayType;

   onClick(data: ChartSeriesDataPoint) {
      this.chartClick.emit(data);
   }

   onSelect($event) {
      this.select.emit($event);
   }

   constructor(
      protected formServiceFactory: ReportParameterFormFactoryService,
      protected store: Store,
      public changeDetectorRef: ChangeDetectorRef,
      protected fb: UntypedFormBuilder,
      public graphTileService: GraphTileService<U>,
      protected router: Router
   ) {
      super(formServiceFactory, store, changeDetectorRef, fb, router);
      this.graphTileService.$init.pipe(takeUntil(this.destroyed$)).subscribe(() => {
         this.handleGraphTileInit();
      });
   }

   // Overrides the graphTileService fields to keep graph tiles consistent with rules around lables, timeline, view, legendPosition, etc.
   private handleGraphTileInit(): void {
      this.graphTileService.labels = false;
      this.graphTileService.timeline = false;
      this.graphTileService.view = undefined;
      this.graphTileService.rotateXAxisTicks = false;

      if (
         this.chartDisplayType === ChartDisplayType.VerticalBar ||
         (this.chartDisplayType === ChartDisplayType.VerticalLine && this.graphTileService.data.length === 1)
      ) {
         this.graphTileService.colorScheme = {
            ...defaultColorScheme,
            domain: [this.tileConfig?.dashboardTileLayoutConfiguration.color ?? '#2b376b'],
         };
      } else if (this.chartDisplayType === ChartDisplayType.Pie) {
         this.graphTileService.colorScheme =
            this.graphTileService.colorScheme &&
            JSON.stringify(this.graphTileService.colorScheme) !== JSON.stringify(defaultColorScheme)
               ? this.graphTileService.colorScheme
               : {
                    ...defaultColorScheme,
                    domain: PieGraphTileDefaultColors,
                 };
      }

      this.graphTileService.legend =
         this.chartDisplayType === ChartDisplayType.Pie && this.viewMode !== TileViewModes.Small;

      this.hasBelowLegendPosition =
         this.tileConfig.dashboardTileLayoutConfiguration.width <=
         this.tileConfig.dashboardTileLayoutConfiguration.height;
      this.graphTileService.legendPosition = this.hasBelowLegendPosition ? LegendPosition.Below : LegendPosition.Right;

      this.changeDetectorRef.detectChanges();
   }
}
